import { useEffect, useState, useMemo, useCallback, useRef, type ChangeEvent } from "react";
import type { FC } from "react";
import { Button, Modal, Text } from "@uhg-harmony/react";
import type { IDropdownItem, ModalProps } from "@uhg-harmony/react";
import { ValidatedDropdown } from "../ui/ValidatedDropdown";
import { ValidatedTextInput } from "../ui/ValidatedTextInput";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  setCategory,
  setFolderName,
  setFilename,
  selectFolderNames,
  selectFolderName,
  selectSelectedFolder,
  selectCategory,
} from "../../redux/metadata/metadataSlice";
import {
  selectAvailableCategories,
  selectIsClientSelected,
  selectIsPlatformSelected,
  selectUserRole,
} from "../../redux/user";
import { selectDataByCategory, transformFoldersToTreeData } from "../../redux/templates/templatesTreeSelectors";
import {
  FOLDER_NAME_MIN_LENGTH,
  FOLDER_NAME_MAX_LENGTH,
  FOLDER_NAME_REGEX,
  TEMPLATE_NAME_MIN_LENGTH,
  TEMPLATE_NAME_MAX_LENGTH,
  TEMPLATE_NAME_REGEX,
  sanitizeFolderName,
  sanitizeTemplateName,
  normalizeForComparison,
} from "../utils/nameValidation";
import { mapUICategoryToAPI } from "../utils/categoryMapping";
import { ErrorContainer } from "./shared";
import styles from "./BaseTemplateFormModal.module.css";

export interface BaseTemplateFormData {
  templateName: string;
  category: string;
  folderName: string;
}

const DEFAULT_FOLDER_NAME = "Drafts";


type BaseTemplateFormModalProps = ModalProps & {
  open: boolean;
  onClose: () => void;
  onConfirm: (formData: BaseTemplateFormData) => void;
  initialTemplateName?: string;
  displayTemplateName?: string;
  confirmButtonText?: string;
  isLoading?: boolean;
  errorMessage?: string;
  showCategoryDropdown?: boolean;
  showFolderDropdown?: boolean;
  defaultFolderSelection?: string; // Set to pre-select a folder (e.g. "Draft"). When omitted, Redux folder name is used instead.
  isTemplateNameDisabled?: boolean; // When true, all form fields are read-only (e.g. after initial creation)
  currentTemplateId?: string; // ID of the template being edited/renamed (to exclude from duplicate check)
  fixedCategory?: string; // When set, category dropdown is shown with exactly one option.
};

/**
 * Base modal component for template operations (SaveAsDraft, Clone, Move, etc.)
 * Provides common form structure with:
 * - Template name input with validation
 * - Category dropdown (optional)
 * - Folder dropdown with "Create New Folder" option (optional)
 * - New folder name input with duplicate validation
 * - Redux integration for categories and folder names
 * - Form validation and error handling
 */
export const BaseTemplateFormModal: FC<BaseTemplateFormModalProps> = ({
  title,
  open,
  onClose,
  onConfirm,
  initialTemplateName = "",
  displayTemplateName = "",
  confirmButtonText = "Save",
  isLoading = false,
  errorMessage = "",
  showCategoryDropdown = true,
  showFolderDropdown = true,
  defaultFolderSelection = "",
  isTemplateNameDisabled = false,
  currentTemplateId,
  fixedCategory,
}) => {
  const dispatch = useAppDispatch();

  // Get values from Redux
  const folderName = useAppSelector(selectFolderName);
  const selectedFolderFromTree = useAppSelector(selectSelectedFolder);
  const category = useAppSelector(selectCategory);
  const availableCategories = useAppSelector(selectAvailableCategories);
  const activeFolderNames = useAppSelector(selectFolderNames); // Computed by tree view based on context
  const templatesDataByCategory = useAppSelector(selectDataByCategory); // For duplicate validation and category-specific folders
  const isClientSelected = useAppSelector(selectIsClientSelected);
  const isPlatformSelected = useAppSelector(selectIsPlatformSelected);
  const userRole = useAppSelector(selectUserRole);

  const modalAvailableCategories = useMemo(() => {
    if (userRole !== "TB_ECP_ADMIN") {
      return availableCategories;
    }

    // For ECP Admin, always include Global in modal dropdown in addition to existing role/context categories.
    const unique = new Set(["Global", ...availableCategories]);
    return Array.from(unique);
  }, [availableCategories, userRole]);

  // Convert available categories to dropdown items
  // availableCategories already filtered by role and account/client selections in selector
  const categoryItems = useMemo(() => {
    if (showCategoryDropdown && fixedCategory) {
      return [
        {
          id: "1",
          label: fixedCategory,
          value: fixedCategory,
        },
      ];
    }

    return modalAvailableCategories.map((cat, index) => ({
      id: String(index + 1),
      label: cat,
      value: cat,
    }));
  }, [modalAvailableCategories, showCategoryDropdown, fixedCategory]);

  const [selectedCategory, setSelectedCategory] =
    useState<IDropdownItem | null>(
      categoryItems.length > 0 ? categoryItems[0] : null,
    );

  // Form state
  const [folderNameBlurred, setFolderNameBlurred] = useState(false);
  const [templateNameBlurred, setTemplateNameBlurred] = useState(false);
  const [folderNameHasInvalidChars, setFolderNameHasInvalidChars] = useState(false);
  const [templateNameHasInvalidChars, setTemplateNameHasInvalidChars] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [editableTemplateName, setEditableTemplateName] = useState(
    initialTemplateName || "",
  );

  // Update editable template name when prop changes
  useEffect(() => {
    setEditableTemplateName(initialTemplateName || "");
  }, [initialTemplateName]);

  // Tracks whether the folder dropdown has been initialized for this open session
  const folderInitializedRef = useRef(false);
  
  // Tracks if user has manually changed the category dropdown (vs auto-selection on mount)
  const categoryManuallyChangedRef = useRef(false);

  // Reset state when modal opens.
  // NOTE: initialTemplateName is intentionally excluded from the deps reset
  // to avoid wiping selectedFolder when the name prop updates while modal is open.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;

    setEditableTemplateName(initialTemplateName || "");
    setNewFolderName("");
    setSelectedFolder(null);
    folderInitializedRef.current = false;
    categoryManuallyChangedRef.current = false;
    setFolderNameBlurred(false);
    setFolderNameHasInvalidChars(false);
    setTemplateNameBlurred(false);
    setTemplateNameHasInvalidChars(false);
  }, [open]);

  // Folder dropdown items - uses tree context folders unless user manually changes category
  const folderItems: IDropdownItem[] = useMemo(() => {
    let folderNames: string[];
    
    // Only compute category-specific folders if user has manually changed the category
    if (showCategoryDropdown && categoryManuallyChangedRef.current && selectedCategory) {
      const categoryValue = selectedCategory.value as string;
      const apiCategory = mapUICategoryToAPI(categoryValue);
      
      // Compute folders for the selected category
      let folders;
      if (apiCategory === 'OPTUM') {
        folders = templatesDataByCategory.OPTUM?.globalTemplates;
      } else if (apiCategory === 'PLATFORM') {
        const source = templatesDataByCategory.PLATFORM || templatesDataByCategory.CLIENT;
        folders = source?.platformTemplates;
      } else if (apiCategory === 'CLIENT') {
        folders = templatesDataByCategory.CLIENT?.clientTemplates;
      }
      
      // Apply the same filtering as tree view - only folders with valid templates
      const treeData = transformFoldersToTreeData(folders);
      folderNames = treeData.map((category) => category.label);
    } else {
      // Use tree context folders (default - what's visible in the tree)
      folderNames = activeFolderNames;
    }
    
    return [
      { id: "new-folder", label: "Create New Folder", value: "newFolder" },
      ...folderNames.map((name) => ({
        id: name,
        label: name,
        value: name,
      })),
    ];
  }, [showCategoryDropdown, selectedCategory, templatesDataByCategory, activeFolderNames]);

  const [selectedFolder, setSelectedFolder] = useState<IDropdownItem | null>(
    null,
  );

  // Initialize folder selection from Redux state or default — runs once per open session.
  // The ref guard prevents this from re-running (and overriding the user's choice)
  // when folderItems or folderName changes after the user has picked a folder.
  // NOTE: folderName is intentionally included in deps so that if Redux dispatches
  // setFolderName and folderItems in the same render cycle (e.g. on first page load),
  // the effect retries and correctly pre-selects the folder once both are available.
  useEffect(() => {
    if (!open || folderInitializedRef.current) return;
    if (folderItems.length <= 1) return; // only "Create New Folder" present — wait for API data
    // Wait for categories to load before initializing folder
    if (showCategoryDropdown && categoryItems.length === 0) return;

    // Priority 1: Use the currently selected folder from the tree view
    if (selectedFolderFromTree) {
      const folderFromTree = folderItems.find(
        (item) =>
          normalizeForComparison(item.label) === normalizeForComparison(selectedFolderFromTree),
      );
      if (folderFromTree) {
        setSelectedFolder(folderFromTree);
        folderInitializedRef.current = true;
        return;
      }
    }

    // Priority 2: Pre-fill from the Redux folder (e.g. the template's current folder)
    if (folderName) {
      const folderFromRedux = folderItems.find(
        (item) =>
          normalizeForComparison(item.label) === normalizeForComparison(folderName),
      );
      if (folderFromRedux) {
        setSelectedFolder(folderFromRedux);
        folderInitializedRef.current = true;
        return;
      }
    }

    // Priority 3: Fall back to defaultFolderSelection (e.g. "Drafts").
    // Uses case-insensitive prefix match so "Drafts" matches a folder named "Draft" and vice versa.
    if (defaultFolderSelection) {
      const needle = normalizeForComparison(defaultFolderSelection);
      const defaultItem = folderItems.find((item) => {
        const hay = normalizeForComparison(item.label);
        return (
          hay === needle || hay.startsWith(needle) || needle.startsWith(hay)
        );
      });
      if (defaultItem) setSelectedFolder(defaultItem);
    }
    folderInitializedRef.current = true;
  }, [open, folderItems, folderName, selectedFolderFromTree, defaultFolderSelection, showCategoryDropdown, categoryItems.length]);

  // Precompute normalized existing folder names for the selected category
  const existingFolderNames = useMemo(() => {
    const names = folderItems
      .filter((item) => item.value !== "newFolder")
      .map((item) => normalizeForComparison(item.label));
    return new Set(names);
  }, [folderItems]);

  useEffect(() => {
    if (categoryItems.length > 0) {
      // Priority: Client (when selected) > Account (when account selected) > Global > first option
      const clientCategory = categoryItems.find(item => item.value === "Client");
      const accountCategory = categoryItems.find(item => item.value === "Account");
      const globalCategory = categoryItems.find(item => item.value === "Global");

      if (isClientSelected && clientCategory) {
        setSelectedCategory(clientCategory);
      } else if (isPlatformSelected && accountCategory) {
        setSelectedCategory(accountCategory);
      } else if (globalCategory) {
        setSelectedCategory(globalCategory);
      } else {
        // Otherwise, auto-select first option
        setSelectedCategory(categoryItems[0]);
      }
      // Reset folder initialization when category auto-selects
      folderInitializedRef.current = false;
    } else {
      setSelectedCategory(null);
    }
  }, [categoryItems, isClientSelected, isPlatformSelected]);

  // Form validation helpers
  const isCreatingNewFolder = selectedFolder?.value === "newFolder";

  // Check for duplicate template names in the selected folder
  const isDuplicateTemplateName = useMemo(() => {
    const normalizedTemplateName = normalizeForComparison(editableTemplateName);

    // Need at least a template name to check
    if (!normalizedTemplateName) {
      return false;
    }

    // Determine which folder to check (prioritize selections, fall back to Redux)
    const folderToCheck = isCreatingNewFolder
      ? newFolderName.trim()
      : ((selectedFolder?.value as string | undefined) ?? folderName);

    // If no folder is determined yet, can't check for duplicates
    if (!folderToCheck) return false;

    const normalizedFolder = normalizeForComparison(folderToCheck);

    // Determine category (prioritize selection, fall back to Redux)
    const categoryValue = (selectedCategory?.value as string) || category;

    // If no category is determined, can't check for duplicates
    if (!categoryValue) return false;

    // Map UI category to API category
    const apiCategory = mapUICategoryToAPI(categoryValue);
    const templatesData = templatesDataByCategory[apiCategory];

    if (!templatesData) return false;

    // Collect all template folders from all template types (global, platform, client)
    const allFolders = [
      ...(templatesData.globalTemplates || []),
      ...(templatesData.platformTemplates || []),
      ...(templatesData.clientTemplates || []),
    ];

    // Find the folder that matches
    const matchingFolder = allFolders.find(
      (folder) => normalizeForComparison(folder.folderName) === normalizedFolder,
    );

    if (!matchingFolder) return false;

    // Check if any template in this folder has the same name
    // Exclude the current template being edited (if currentTemplateId is provided)
    return matchingFolder.templates.some(
      (template) =>
        normalizeForComparison(template.templateName) === normalizedTemplateName &&
        template.templateId !== currentTemplateId,
    );
  }, [
    editableTemplateName,
    selectedCategory,
    selectedFolder,
    isCreatingNewFolder,
    newFolderName,
    folderName,
    category,
    templatesDataByCategory,
    currentTemplateId,
  ]);

  // Event handlers
  const onCategoryChange = useCallback(
    (item: IDropdownItem | null) => {
      setSelectedCategory(item);
      // Mark category as manually changed by user
      categoryManuallyChangedRef.current = true;
      // Reset folder selection so folderItems re-initializes for the new category
      setSelectedFolder(null);
      folderInitializedRef.current = false;
      if (folderNameBlurred) {
        setFolderNameBlurred(false);
      }
    },
    [folderNameBlurred],
  );

  const onFolderChange = useCallback(
    (item: IDropdownItem | null) => {
      setSelectedFolder(item);
      if (folderNameBlurred) {
        setFolderNameBlurred(false);
      }
      if (item && item.value !== "newFolder") {
        dispatch(setFolderName({ folderName: String(item.value) }));
      }
    },
    [folderNameBlurred, dispatch],
  );

  const onNewFolderNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const sanitized = sanitizeFolderName(raw);
      setFolderNameHasInvalidChars(raw !== sanitized);
      setNewFolderName(sanitized);

      if (folderNameBlurred && sanitized.trim().length > 0) {
        setFolderNameBlurred(false);
      }
    },
    [folderNameBlurred],
  );

  const onTemplateNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;
      const next = sanitizeTemplateName(event.target.value);
      setTemplateNameHasInvalidChars(raw !== next);
      setEditableTemplateName(next);
      if (templateNameBlurred && next.trim().length > 0) {
        setTemplateNameBlurred(false);
      }
    },
    [templateNameBlurred],
  );

  // Form validation
  const isDuplicateFolder =
    isCreatingNewFolder &&
    newFolderName.trim().length > 0 &&
    existingFolderNames.has(normalizeForComparison(newFolderName));

  const isReservedFolderName =
    isCreatingNewFolder &&
    newFolderName.trim().length > 0 &&
    normalizeForComparison(newFolderName).includes("draft");

  const isFolderNameFormatValid =
    !isCreatingNewFolder ||
    (newFolderName.length === 0 || FOLDER_NAME_REGEX.test(newFolderName));
  const hasMultipleFolderSpaces = /\s{2,}/.test(newFolderName);

  const isFolderNameTooShort =
    isCreatingNewFolder &&
    newFolderName.trim().length > 0 &&
    newFolderName.trim().length < FOLDER_NAME_MIN_LENGTH;

  const isFolderNameTooLong =
    isCreatingNewFolder && newFolderName.length > FOLDER_NAME_MAX_LENGTH;

  const hasValidNewFolderName =
    !isCreatingNewFolder ||
    (newFolderName.trim().length > 0 &&
      !isFolderNameTooShort &&
      !isFolderNameTooLong &&
      !hasMultipleFolderSpaces &&
      isFolderNameFormatValid &&
      !folderNameHasInvalidChars &&
      !isDuplicateFolder &&
      !isReservedFolderName);

  const isTemplateNameTooLong =
    editableTemplateName.length > TEMPLATE_NAME_MAX_LENGTH;
  const isTemplateNameTooShort =
    editableTemplateName.trim().length > 0 &&
    editableTemplateName.trim().length < TEMPLATE_NAME_MIN_LENGTH;
  const hasMultipleTemplateSpaces = /\s{2,}/.test(editableTemplateName);
  const isTemplateNameFormatValid =
    editableTemplateName.length === 0 || TEMPLATE_NAME_REGEX.test(editableTemplateName);

  const hasValidTemplateName =
    editableTemplateName.trim().length >= TEMPLATE_NAME_MIN_LENGTH &&
    !isTemplateNameTooLong &&
    !isTemplateNameTooShort &&
    !hasMultipleTemplateSpaces &&
    isTemplateNameFormatValid &&
    !templateNameHasInvalidChars &&
    (!isDuplicateTemplateName || isTemplateNameDisabled);

  const isFormValid =
    hasValidTemplateName &&
    (!showFolderDropdown || (hasValidNewFolderName && !!selectedFolder)) &&
    (!showCategoryDropdown || !!selectedCategory);

  const validationMessages = useMemo(() => {
    const messages: string[] = [];

    if (templateNameBlurred && editableTemplateName.trim().length === 0 && !isTemplateNameDisabled) {
      messages.push("Name: Input is required.");
    }
    if (isDuplicateTemplateName && !isTemplateNameDisabled) {
      messages.push("Name with same category already exists in the destination folder.");
    }
    if (isTemplateNameTooLong && !isTemplateNameDisabled) {
      messages.push(`Name cannot exceed ${TEMPLATE_NAME_MAX_LENGTH} characters.`);
    }
    if (isTemplateNameTooShort && !isTemplateNameDisabled) {
      messages.push(`Name must be at least ${TEMPLATE_NAME_MIN_LENGTH} characters.`);
    }
    if (hasMultipleTemplateSpaces && !isTemplateNameDisabled) {
      messages.push("Only one space is allowed between words.");
    }
    if (!isTemplateNameFormatValid && !hasMultipleTemplateSpaces && !templateNameHasInvalidChars && !isTemplateNameDisabled) {
      messages.push("Name format is invalid. It must start and end with a letter or number, with no leading, trailing, or consecutive spaces or be all-numeric.");
    }
    if (templateNameHasInvalidChars && !isTemplateNameDisabled) {
      messages.push("Name contains invalid characters. Only letters, numbers, spaces, underscores, hyphens, and tildes are allowed.");
    }

    if (showCategoryDropdown && folderNameBlurred && !selectedCategory) {
      messages.push("Category: Selection is required.");
    }

    if (showFolderDropdown && folderNameBlurred && !selectedFolder) {
      messages.push("Folder: Selection is required.");
    }

    if (isCreatingNewFolder) {
      if (folderNameBlurred && newFolderName.trim().length === 0) {
        messages.push("New Folder Name: Input is required.");
      }
      if (isDuplicateFolder) {
        messages.push("A folder with this name already exists.");
      }
      if (isReservedFolderName) {
        messages.push('"Drafts" is a reserved folder name and cannot be used.');
      }
      if (isFolderNameTooShort) {
        messages.push(`Folder name must be at least ${FOLDER_NAME_MIN_LENGTH} characters.`);
      }
      if (isFolderNameTooLong) {
        messages.push(`Folder name cannot exceed ${FOLDER_NAME_MAX_LENGTH} characters.`);
      }
      if (hasMultipleFolderSpaces) {
        messages.push("Only one space is allowed between folder name words.");
      }
      if (!isFolderNameFormatValid && !hasMultipleFolderSpaces && !folderNameHasInvalidChars) {
        messages.push("Folder name format is invalid. It must start and end with a letter or number, with no leading, trailing, or consecutive spaces or be all-numeric.");
      }
      if (folderNameHasInvalidChars) {
        messages.push("Folder name contains invalid characters. Only letters, numbers, spaces, underscores, and hyphens are allowed.");
      }
    }

    return messages;
  }, [
    editableTemplateName,
    folderNameBlurred,
    folderNameHasInvalidChars,
    isCreatingNewFolder,
    isDuplicateFolder,
    isDuplicateTemplateName,
    isFolderNameFormatValid,
    isFolderNameTooLong,
    isFolderNameTooShort,
    hasMultipleFolderSpaces,
    isReservedFolderName,
    isTemplateNameDisabled,
    isTemplateNameFormatValid,
    isTemplateNameTooLong,
    isTemplateNameTooShort,
    hasMultipleTemplateSpaces,
    newFolderName,
    selectedCategory,
    selectedFolder,
    showCategoryDropdown,
    showFolderDropdown,
    templateNameBlurred,
    templateNameHasInvalidChars,
  ]);

  const handleConfirm = () => {
    if (!isFormValid) {
      setTemplateNameBlurred(true);
      setFolderNameBlurred(true);
      return;
    }

    const rawFolderToUse: string = isCreatingNewFolder
      ? newFolderName.trim()
      : ((selectedFolder?.value as string | undefined) ?? folderName);
    const folderToUse = rawFolderToUse.trim() || DEFAULT_FOLDER_NAME;

    const categoryToUse = (selectedCategory?.value as string) || category;

    // Update metadata in Redux
    if (folderToUse !== folderName) {
      dispatch(setFolderName({ folderName: folderToUse }));
    }
    if (editableTemplateName.trim() !== "") {
      dispatch(setFilename({ filename: editableTemplateName.trim() }));
    }
    if (categoryToUse && categoryToUse !== category) {
      dispatch(setCategory({ category: categoryToUse }));
    }

    onConfirm({
      templateName: editableTemplateName.trim(),
      category: categoryToUse,
      folderName: folderToUse,
    });
  };

  return (
    <>
      {open && (
        <Modal open={open} title={title} aria-label={typeof title === 'string' ? title : 'Dialog'} onClose={onClose}>
          <Modal.Body>
            {errorMessage && (
              <ErrorContainer>
                <strong>Error:</strong> {errorMessage}
              </ErrorContainer>
            )}

            {displayTemplateName && (
              <Text size="medium" style={{ marginBottom: "12px", display: "block" }}>
                <strong>Current Name : {displayTemplateName}</strong>
              </Text>
            )}

            <div className={styles.formBody}>
            <div className={styles.formField}>
              <ValidatedTextInput
                label="Name"
                required
                value={editableTemplateName}
                onChange={onTemplateNameChange}
                placeholder={`MIN ${TEMPLATE_NAME_MIN_LENGTH} | MAX ${TEMPLATE_NAME_MAX_LENGTH} | A-Z 0-9 _ - ~`}
                autoFocus={!isTemplateNameDisabled && confirmButtonText !== "Rename"}
                errorMsgOrientation="top"
                forceBlurred={templateNameBlurred || isDuplicateTemplateName}
                disabled={isTemplateNameDisabled}
              />
            </div>

            {showCategoryDropdown && (
              <div className={styles.formField}>
                <ValidatedDropdown
                  id="template-category-dropdown"
                  label="Category"
                  items={categoryItems}
                  required
                  value={selectedCategory}
                  onChange={onCategoryChange}
                  showClearButton={false}
                  placeholderText="Please select"
                  errorMsgOrientation="top"
                  forceBlurred={folderNameBlurred}
                  hideDefaultRequiredError
                  disabled={isTemplateNameDisabled}
                />
              </div>
            )}

            {showFolderDropdown && (
              <>
                <div className={styles.formField}>
                  <ValidatedDropdown
                    key={
                      selectedFolder
                        ? String(selectedFolder.value)
                        : "no-folder"
                    }
                    id="template-folder-dropdown"
                    label="Folder"
                    items={folderItems}
                    required
                    value={selectedFolder}
                    onChange={onFolderChange}
                    showClearButton={false}
                    placeholderText="Please select"
                    errorMsgOrientation="top"
                    forceBlurred={folderNameBlurred}
                    hideDefaultRequiredError
                    disabled={isTemplateNameDisabled}
                  />
                </div>

                {isCreatingNewFolder && (
                  <div className={styles.formField}>
                    <ValidatedTextInput
                      label="New Folder Name"
                      required
                      value={newFolderName}
                      onChange={onNewFolderNameChange}
                      placeholder={`MIN ${FOLDER_NAME_MIN_LENGTH} | MAX ${FOLDER_NAME_MAX_LENGTH} | A-Z 0-9 _ - | no Drafts`}
                      autoFocus
                      errorMsgOrientation="top"
                      forceBlurred={folderNameBlurred || isDuplicateFolder || isReservedFolderName}
                    />
                  </div>
                )}
              </>
            )}

            </div>

            {validationMessages.length > 0 && (
              <ErrorContainer style={{ marginTop: "20px", marginBottom: 0 }}>
                {validationMessages.map((message) => (
                  <div key={message}>{message}</div>
                ))}
              </ErrorContainer>
            )}
          </Modal.Body>
          <Modal.Actions>
            <Button
              variant="secondary"
              onPress={onClose}
              isDisabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleConfirm}
              isDisabled={isLoading}
            >
              {isLoading
                ? confirmButtonText === "Rename"
                  ? "Renaming..."
                  : confirmButtonText === "Clone"
                    ? "Cloning..."
                    : "Saving..."
                : confirmButtonText}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </>
  );
};
